import { useState, useEffect, useMemo } from "react";
import { Font, Document, Page, StyleSheet, View, Text, Image, pdf } from "@react-pdf/renderer";
import { constants } from "../utils/ranges";
import CompanyLogo from "../assets/images/IndusindLogo.png";
import CareplixLogo from "../assets/images/CarePlixLogo.png";
import Pointer from "../assets/images/report/Pointer.png";
import HeartHealth from "../assets/images/report/heart-health-score.png";
import MentalWellbeing from "../assets/images/report/mental-wellbeing-score.png";
import Physiological from "../assets/images/report/physiological-score.png";
import BloodPressure from "../assets/images/report/blood-pressure.png";
import HeartRate from "../assets/images/report/heart-rate.png";
import RespirationRate from "../assets/images/report/respiration-rate.png";
import OxygenSaturation from "../assets/images/report/oxygen-saturation.png";
import BMI from "../assets/images/report/bmi.png";
import HRV from "../assets/images/report/hrv.png";
import HRRelated from "../assets/images/report/hr-related.png";
import CardiacOutput from "../assets/images/report/cardiac-output.png";
import MAP from "../assets/images/report/map.png";
import HeartUtilization from "../assets/images/report/heart-utilization.png";
import StressIndex from "../assets/images/report/stress-index.png";
import VO2MAX from "../assets/images/report/vo2max.png";

Font.register({
  family: "Poppins",
  fonts: [
    { src: "https://fonts.gstatic.com/s/poppins/v20/pxiByp8kv8JHgFVrLDz8V1tvFP-KUEg.ttf", fontWeight: "light" },
    { src: "https://fonts.gstatic.com/s/poppins/v20/pxiEyp8kv8JHgFVrFJDUc1NECPY.ttf", fontWeight: "normal" },
    { src: "https://fonts.gstatic.com/s/poppins/v20/pxiByp8kv8JHgFVrLGT9V1tvFP-KUEg.ttf", fontWeight: "medium" },
    { src: "https://fonts.gstatic.com/s/poppins/v20/pxiByp8kv8JHgFVrLEj6V1tvFP-KUEg.ttf", fontWeight: "semibold" },
    { src: "https://fonts.gstatic.com/s/poppins/v20/pxiByp8kv8JHgFVrLCz7V1tvFP-KUEg.ttf", fontWeight: "bold" },
  ],
});
Font.registerHyphenationCallback((word) => [word]);

const SampleReport = () => {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  const sampleResult = {
    timestamp: new Date(),
    posture: "resting",
    mind_score: 72,
    heart_score: 85,
    physiological_score: 68,
    bp_sys: 118,
    bp_dia: 76,
    resp_rate: 16,
    oxy_sat_prcnt: 98,
    heart_rate: 72,
    bmi: 23.5,
    sdnn: 65,
    rmssd: 42,
    pNN50_per: 18,
    cardiac_out: 5.2,
    map: 90,
    heart_utilized: 38,
    HRMax: 190,
    HRR: 118,
    THRR: 142,
    vo2max: 38.5,
  };

  const styles = StyleSheet.create({
    page: { backgroundColor: "#fff", flexDirection: "column", alignItems: "stretch", justifyContent: "space-between" },
    header: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      paddingHorizontal: 40,
      paddingVertical: 16,
      borderBottomWidth: 3,
      borderBottomColor: "#ED1C24",
    },
    contentContainer: { flex: 1, paddingHorizontal: 40 },
    title: { fontFamily: "Poppins", fontWeight: "bold", fontSize: 14, color: "#1845a3", marginBottom: 16 },
    horizontalLine: { marginVertical: 8, height: 1, backgroundColor: "#D5D5D5", borderRadius: 999 },
    scoreTitle: { flex: 1, fontFamily: "Poppins", fontSize: 12 },
    trContainer: { flexDirection: "row", gap: 4, paddingVertical: 6, paddingHorizontal: 12 },
    trText: { flex: 1, fontSize: 10, fontFamily: "Poppins" },
    pTitle: { fontFamily: "Poppins", fontWeight: "semibold", fontSize: 12, color: "#1845a3", marginBottom: 4 },
    pDesc: { fontFamily: "Poppins", fontSize: 10, lineHeight: 1.5, textAlign: "justify" },
    pRange: { fontFamily: "Poppins", fontSize: 10, lineHeight: 1.25, textAlign: "justify" },
    footer: {
      paddingVertical: 20,
      paddingHorizontal: 40,
      backgroundColor: "#1845a3",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
    },
    footerText: { fontFamily: "Poppins", fontWeight: "medium", fontSize: 8, color: "#fff" },
  });

  const Range = ({ ranges = [], selectedIndex = -1, containerStyle = {} }) => (
    <View style={[{ flexDirection: "row" }, containerStyle]}>
      {ranges.map((r, index, list) => (
        <View key={index} style={{ flex: 1, alignItems: "center" }}>
          <Text style={{ color: r.color, fontFamily: "Poppins", fontSize: 8, marginBottom: 6, maxLines: 1, textOverflow: "ellipsis", textTransform: "uppercase" }}>{r.label}</Text>
          <View style={{ alignSelf: "stretch", height: 6, backgroundColor: r.color, borderTopLeftRadius: index > 0 ? 0 : 20, borderBottomLeftRadius: index > 0 ? 0 : 20, borderTopRightRadius: index < list.length - 1 ? 0 : 20, borderBottomRightRadius: index < list.length - 1 ? 0 : 20 }} />
          {selectedIndex === index && <Image source={Pointer} style={{ height: 8, marginTop: 4 }} />}
        </View>
      ))}
    </View>
  );

  const Score = ({ icon, title = "", value = 0 }) => {
    const rangeIndex = value >= 75 ? 0 : value >= 50 ? 1 : 2;
    const selectedRange = constants["Scores"].ranges[rangeIndex];
    return (
      <View style={{ flexDirection: "row", alignItems: "center" }}>
        <Image source={icon} style={{ height: 20, width: 20, marginRight: 10 }} />
        <Text style={styles.scoreTitle}>{title}</Text>
        <Text style={[styles.scoreTitle, { color: selectedRange.color, fontWeight: "semibold", textTransform: "uppercase" }]}>{selectedRange.label} Risk</Text>
        <Range containerStyle={{ flexBasis: "45%" }} ranges={constants["Scores"].ranges} selectedIndex={rangeIndex} />
      </View>
    );
  };

  const Table = ({ containerStyle = {}, title = "", data = [] }) => (
    <View style={containerStyle}>
      <View style={[styles.trContainer, { borderTopLeftRadius: 5, borderTopRightRadius: 5, backgroundColor: "#1845a3" }]}>
        <Text style={[styles.trText, { fontWeight: "semibold", color: "#fff" }]}>{title}</Text>
        <Text style={[styles.trText, { fontWeight: "semibold", color: "#fff" }]}>Value</Text>
        <Text style={[styles.trText, { fontWeight: "semibold", color: "#fff" }]}>Measurement Scale</Text>
        <Text style={[styles.trText, { fontWeight: "semibold", color: "#fff" }]}>Range</Text>
      </View>
      {data?.map?.(([title, value, scale, range], index) => (
        <View key={index} style={[styles.trContainer, { backgroundColor: Boolean(index % 2) ? "#f4f5f9" : "transparent" }]}>
          <Text style={styles.trText}>{title}</Text>
          <Text style={styles.trText}>{value}</Text>
          <Text style={[styles.trText, { color: scale?.color ?? "#000" }]}>{scale?.label}</Text>
          <Text style={styles.trText}>{range}</Text>
        </View>
      ))}
    </View>
  );

  const Disclaimer = ({ containerStyle = {} }) => (
    <View style={[{ marginTop: 36 }, containerStyle]}>
      <Text style={{ fontFamily: "Poppins", fontWeight: "semibold", fontSize: 10 }}>Disclaimer</Text>
      <Text style={{ fontFamily: "Poppins", fontSize: 7, lineHeight: 1.25, textAlign: "justify" }}>
        For Investigational Use Only. Careplix is not a substitute for the clinical judgment of a health care professional. Careplix is intended to improve your awareness of general wellness. Careplix does not diagnose, treat, mitigate or prevent any disease, symptom, disorder or abnormal physical state. Consult with a health care professional or emergency services if you believe you may have a medical issue.
      </Text>
    </View>
  );

  const ParameterInfo = ({ icon, title = "", desc = "", ranges = [] }) => (
    <View style={{ flexDirection: "row", marginBottom: ranges.length > 0 ? 16 : 24 }}>
      <Image source={icon} style={{ height: 30, width: 30, marginRight: 10 }} />
      <View style={{ flex: 1 }}>
        <Text style={styles.pTitle}>{title}</Text>
        <Text style={styles.pDesc}>{desc}</Text>
        {ranges.map((r, idx) => (
          <View key={`range-${idx}`} style={{ marginTop: 4, flexDirection: "row" }}>
            <Text style={[styles.pRange, { marginHorizontal: 4 }]}>•</Text>
            <Text style={[styles.pRange, { flex: 1 }]}>
              <Text style={{ fontWeight: "semibold" }}>{r.label}: </Text>
              <Text>{r.desc}</Text>
            </Text>
          </View>
        ))}
      </View>
    </View>
  );

  const bpRiskSelIdx = sampleResult.bp_sys < 108 || sampleResult.bp_sys > 135 ? 2 : sampleResult.bp_sys < 115 || sampleResult.bp_sys > 125 ? 1 : 0;

  const reportDoc = useMemo(() => (
    <Document title="CarePlix Sample Report" subject="Sample Report" author="CarePlix">
      <Page wrap={false} style={{ backgroundColor: "#f8faff" }}>
        <View style={{ height: "100%", width: "100%", flexDirection: "column", alignItems: "center", justifyContent: "space-between", paddingVertical: 48, paddingHorizontal: 40 }}>
          <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "center", gap: 16, width: "100%" }}>
            <Image source={CompanyLogo} style={{ height: 48 }} />
            <View style={{ width: 1, height: 36, backgroundColor: "#d1d5db" }} />
            <View>
              <Text style={{ fontFamily: "Poppins", fontSize: 8, color: "#6b7280" }}>Powered by</Text>
              <Image source={CareplixLogo} style={{ height: 20 }} />
            </View>
          </View>
          <View style={{ alignItems: "center", paddingHorizontal: 20 }}>
            <View style={{ width: 80, height: 4, borderRadius: 2, backgroundColor: "#1845a3", marginBottom: 24 }} />
            <Text style={{ fontFamily: "Poppins", fontWeight: "bold", fontSize: 32, color: "#1845a3", textAlign: "center", lineHeight: 1.3 }}>Digital Health</Text>
            <Text style={{ fontFamily: "Poppins", fontWeight: "bold", fontSize: 32, color: "#ED1C24", textAlign: "center", lineHeight: 1.3 }}>Assessment Report</Text>
            <View style={{ width: 80, height: 4, borderRadius: 2, backgroundColor: "#ED1C24", marginTop: 24 }} />
            <Text style={{ fontFamily: "Poppins", fontSize: 11, color: "#6b7280", marginTop: 20, textAlign: "center" }}>A comprehensive overview of your health vitals and wellness parameters</Text>
          </View>
          <View style={{ width: "100%", paddingVertical: 20, paddingHorizontal: 24, backgroundColor: "#ffffff", borderRadius: 12, borderWidth: 1, borderColor: "#e5e7eb" }}>
            <View style={{ flexDirection: "row", justifyContent: "space-between", marginBottom: 12 }}>
              <View>
                <Text style={{ fontFamily: "Poppins", fontSize: 8, color: "#9ca3af" }}>Customer Name</Text>
                <Text style={{ fontFamily: "Poppins", fontWeight: "semibold", fontSize: 14, color: "#1845a3" }}>Rajesh Kumar</Text>
              </View>
              <View style={{ alignItems: "flex-end" }}>
                <Text style={{ fontFamily: "Poppins", fontSize: 8, color: "#9ca3af" }}>Agent Name</Text>
                <Text style={{ fontFamily: "Poppins", fontWeight: "semibold", fontSize: 14, color: "#ED1C24" }}>Amit Sharma</Text>
              </View>
            </View>
            <View style={{ height: 1, backgroundColor: "#e5e7eb", marginBottom: 12 }} />
            <View style={{ flexDirection: "row", justifyContent: "space-between" }}>
              <View>
                <Text style={{ fontFamily: "Poppins", fontSize: 8, color: "#9ca3af" }}>Assessment Date</Text>
                <Text style={{ fontFamily: "Poppins", fontWeight: "medium", fontSize: 11, color: "#374151" }}>{sampleResult.timestamp.toLocaleDateString(undefined, { day: "2-digit", month: "long", year: "numeric", timeZone: "Asia/Kolkata" })}</Text>
              </View>
              <View style={{ alignItems: "center" }}>
                <Text style={{ fontFamily: "Poppins", fontSize: 8, color: "#9ca3af" }}>Assessment Time</Text>
                <Text style={{ fontFamily: "Poppins", fontWeight: "medium", fontSize: 11, color: "#374151" }}>{sampleResult.timestamp.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: true, timeZone: "Asia/Kolkata" })}</Text>
              </View>
            </View>
          </View>
          <Text style={{ fontFamily: "Poppins", fontSize: 7, color: "#9ca3af", textAlign: "center" }}>IndusInd General Insurance Company Limited | Powered by CarePlix</Text>
        </View>
      </Page>

      <Page style={styles.page}>
        <View fixed style={styles.header}>
          <View style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
            <Image source={CompanyLogo} style={{ height: 40 }} />
            <View style={{ width: 1, height: 28, backgroundColor: "#d1d5db" }} />
            <View>
              <Text style={{ fontFamily: "Poppins", fontSize: 6, color: "#6b7280" }}>Powered by</Text>
              <Image source={CareplixLogo} style={{ height: 14 }} />
            </View>
          </View>
          <View style={{ alignItems: "flex-end" }}>
            <Text style={{ fontFamily: "Poppins", fontSize: 10 }}><Text style={{ fontWeight: "semibold", color: "#6b7280" }}>Date: </Text><Text style={{ color: "#374151" }}>{sampleResult.timestamp.toLocaleDateString(undefined, { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "Asia/Kolkata" })}</Text></Text>
            <Text style={{ fontFamily: "Poppins", fontSize: 10 }}><Text style={{ fontWeight: "semibold", color: "#6b7280" }}>Time: </Text><Text style={{ color: "#374151" }}>{sampleResult.timestamp.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: true, timeZone: "Asia/Kolkata" })}</Text></Text>
          </View>
        </View>
        <View style={styles.contentContainer}>
          <View style={{ flexDirection: "row", gap: 48 }}>
            <View style={{ flex: 1 }}>
              {[
                { label: "Relation", value: "Self" },
                { label: "Age", value: "35 years" },
                { label: "Gender", value: "Male" },
                { label: "Height", value: "175 cms" },
                { label: "Weight", value: "72 kgs" },
              ].map(({ label, value }, index) => (
                <Text key={label} style={{ paddingTop: index > 0 ? 8 : 0, paddingBottom: 8, borderBottomWidth: 0.5, borderBottomColor: "#D4D4D4", fontFamily: "Poppins", fontSize: 12 }}>
                  <Text style={{ fontWeight: "semibold" }}>{label}: </Text>
                  <Text>{value}</Text>
                </Text>
              ))}
            </View>
            <View style={{ flex: 1, paddingVertical: 24, paddingHorizontal: 36, borderRadius: 20, backgroundColor: "#f8f9fc", textAlign: "center" }}>
              <Text style={{ fontFamily: "Poppins", fontWeight: "bold", fontSize: 16, color: "#1845a3" }}>Blood Pressure Risk</Text>
              <Text style={{ fontFamily: "Poppins", fontSize: 10, lineHeight: 1.25, marginTop: 4, marginBottom: 16 }}>
                <Text>Your Blood Pressure looks to be{"\n"}</Text>
                <Text style={{ fontWeight: "semibold" }}>within</Text>
                <Text> the normal range.</Text>
              </Text>
              <Range ranges={constants["Scores"].ranges} selectedIndex={bpRiskSelIdx} />
            </View>
          </View>
          <Text style={[styles.title, { marginTop: 24 }]}>Other Health Scores</Text>
          <Score icon={HeartHealth} title="Heart Health" value={sampleResult.heart_score} />
          <View style={styles.horizontalLine} />
          <Score icon={MentalWellbeing} title="Mental Wellbeing" value={sampleResult.mind_score} />
          <View style={styles.horizontalLine} />
          <Score icon={Physiological} title="Physiological" value={sampleResult.physiological_score} />
          <Text style={[styles.title, { marginTop: 24 }]}>Health Parameters</Text>
          <Table
            title="Key Body Vitals"
            data={[
              ["Blood Pressure", "118/76 mmHg", constants["Blood Pressure"].ranges[0].reduce((p, c) => (118 > c.point ? c : p), 0), "90-120/60-80 mmHg"],
              ["Respiration Rate", "16/min", constants["Respiration Rate"].ranges.reduce((p, c) => (16 >= c.point ? c : p)), "12-20/min"],
              ["Oxygen Saturation", "98%", constants["Oxygen Saturation"].ranges.reduce((p, c) => (98 >= c.point ? c : p)), "95-100%"],
            ]}
          />
          <Disclaimer />
        </View>
        <View fixed style={styles.footer}>
          <Text style={styles.footerText}>The Report generated by CarePlix on behalf of Indusind General Insurance.</Text>
        </View>
      </Page>

      <Page wrap={false} style={{ backgroundColor: "#f8faff" }}>
        <View style={{ height: "100%", width: "100%", flexDirection: "column", alignItems: "center", justifyContent: "space-between", paddingVertical: 48, paddingHorizontal: 40 }}>
          <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "center", gap: 16, width: "100%" }}>
            <Image source={CompanyLogo} style={{ height: 48 }} />
            <View style={{ width: 1, height: 36, backgroundColor: "#d1d5db" }} />
            <View>
              <Text style={{ fontFamily: "Poppins", fontSize: 8, color: "#6b7280" }}>Powered by</Text>
              <Image source={CareplixLogo} style={{ height: 20 }} />
            </View>
          </View>
          <View style={{ alignItems: "center", paddingHorizontal: 24 }}>
            <Text style={{ fontFamily: "Poppins", fontWeight: "bold", fontSize: 28, color: "#1845a3", textAlign: "center", marginBottom: 24 }}>Thank You!</Text>
            <View style={{ width: 60, height: 3, borderRadius: 2, backgroundColor: "#ED1C24", marginBottom: 24 }} />
            <Text style={{ fontFamily: "Poppins", fontWeight: "medium", fontSize: 14, color: "#374151", textAlign: "center", lineHeight: 1.6 }}>Thanks for doing your Digital Health Assessment!</Text>
            <Text style={{ fontFamily: "Poppins", fontSize: 12, color: "#6b7280", textAlign: "center", lineHeight: 1.6, marginTop: 16 }}>Kindly connect with the advisor/partner to protect your health and other services offered by IGICL.</Text>
            <View style={{ marginTop: 32, paddingVertical: 16, paddingHorizontal: 32, backgroundColor: "#ffffff", borderRadius: 12, borderWidth: 1, borderColor: "#e5e7eb", alignItems: "center" }}>
              <Text style={{ fontFamily: "Poppins", fontSize: 9, color: "#9ca3af", marginBottom: 4 }}>Your Advisor</Text>
              <Text style={{ fontFamily: "Poppins", fontWeight: "bold", fontSize: 18, color: "#1845a3" }}>Amit Sharma</Text>
            </View>
          </View>
          <View style={{ alignItems: "center" }}>
            <Text style={{ fontFamily: "Poppins", fontSize: 7, color: "#9ca3af", textAlign: "center" }}>IndusInd General Insurance Company Limited | Powered by CarePlix</Text>
          </View>
        </View>
      </Page>
    </Document>
  ), []);

  useEffect(() => {
    const generatePdf = async () => {
      try {
        const blob = await pdf(reportDoc).toBlob();
        const url = URL.createObjectURL(blob);
        setPdfUrl(url);
      } catch (err) {
        console.error("PDF generation error:", err);
      } finally {
        setLoading(false);
      }
    };
    generatePdf();
  }, [reportDoc]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <div className="bg-gradient-to-r from-[#1845a3] to-[#ED1C24] px-5 py-4">
        <h1 className="text-white text-lg font-semibold">Sample PDF Report Preview</h1>
        <p className="text-white/60 text-xs mt-1">Preview of the redesigned report</p>
      </div>
      <div className="flex-1 p-4">
        {loading ? (
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#1845a3] mx-auto mb-4"></div>
              <p className="text-gray-500">Generating sample report...</p>
            </div>
          </div>
        ) : pdfUrl ? (
          <iframe
            src={pdfUrl}
            className="w-full rounded-lg shadow-lg border"
            style={{ height: "calc(100vh - 120px)" }}
            title="Sample PDF Report"
          />
        ) : (
          <p className="text-red-500 text-center mt-8">Failed to generate report</p>
        )}
      </div>
    </div>
  );
};

export default SampleReport;
